import { Link } from '@tanstack/react-router'
import { useCases, useDocuments, useReminders, useSystemStatus } from '../api/hooks'
import { TIER_LABELS } from '../api/models'
import {
  Badge,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Section,
  formatAmount,
  formatBytes,
  formatDate,
  formatDateTime,
} from '../components/ui'

export function OverviewPage() {
  const documents = useDocuments({ limit: 6 })
  const cases = useCases({ status: 'open', limit: 5 })
  const due = useReminders('due')
  const status = useSystemStatus()

  return (
    <>
      <h1 className="mb-6 text-2xl">Übersicht</h1>

      <Section title="Status" description="Modell-Tiers, Warteschlange und Speicher.">
        {status.isPending ? (
          <LoadingBlock label="Status wird geladen" />
        ) : status.isError ? (
          <ErrorState error={status.error} onRetry={() => void status.refetch()} />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-paper-500">Dokumente</p>
              <p className="mt-1 font-display text-2xl">{status.data.documents}</p>
              <p className="hint">{status.data.cases} Vorgänge</p>
            </div>
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-paper-500">Warteschlange</p>
              <p className="mt-1 font-display text-2xl">{status.data.queue.depth as number}</p>
              <p className="hint">
                {String(status.data.queue.waiting_for_model ?? 0)} warten auf ein Modell
              </p>
            </div>
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-paper-500">Fällige Erinnerungen</p>
              <p className="mt-1 font-display text-2xl">{status.data.reminders_due}</p>
              <p className="hint">
                {status.data.notifier_enabled ? 'Push aktiv' : 'Benachrichtigungen erst mit den Apps'}
              </p>
            </div>
            <div className="card">
              <p className="text-xs uppercase tracking-wide text-paper-500">Speicher</p>
              <p className="mt-1 font-display text-2xl">{formatBytes(status.data.storage_bytes)}</p>
              <p className="hint">Embeddings: {status.data.embedding_model}</p>
            </div>
          </div>
        )}

        {status.data ? (
          <ul className="mt-3 flex flex-wrap gap-2">
            {status.data.tiers.map((tier) => (
              <li key={tier.tier} className="chip">
                <span
                  aria-hidden
                  className={`h-2 w-2 rounded-full ${tier.online ? 'bg-emerald-500' : 'bg-paper-300'}`}
                />
                {TIER_LABELS[tier.tier] ?? tier.tier}
                <span className="sr-only">{tier.online ? 'online' : 'offline'}</span>
                {!tier.window_open ? <Badge tone="warn">Zeitfenster zu</Badge> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      <Section
        title="Fällige Erinnerungen"
        actions={
          <Link to="/erinnerungen" className="btn-ghost">
            Alle ansehen
          </Link>
        }
      >
        {due.isPending ? (
          <LoadingBlock />
        ) : due.isError ? (
          <ErrorState error={due.error} onRetry={() => void due.refetch()} />
        ) : due.data.length === 0 ? (
          <EmptyState title="Nichts fällig" description="Keine offenen Fristen." />
        ) : (
          <ul className="space-y-2">
            {due.data.slice(0, 5).map((reminder) => (
              <li key={reminder.id} className="card flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{reminder.title}</p>
                  <p className="text-xs text-paper-500">{formatDateTime(reminder.due_at)}</p>
                </div>
                {reminder.close_suggested_by ? <Badge tone="ok">Abschluss vorgeschlagen</Badge> : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Offene Vorgänge"
        actions={
          <Link to="/vorgaenge" className="btn-ghost">
            Alle Vorgänge
          </Link>
        }
      >
        {cases.isPending ? (
          <LoadingBlock />
        ) : cases.isError ? (
          <ErrorState error={cases.error} onRetry={() => void cases.refetch()} />
        ) : cases.data.items.length === 0 ? (
          <EmptyState title="Keine offenen Vorgänge" />
        ) : (
          <ul className="space-y-2">
            {cases.data.items.map((item) => (
              <li key={item.id} className="card">
                <Link to="/vorgaenge/$caseId" params={{ caseId: item.id }} className="font-medium hover:underline">
                  {item.title || 'Ohne Titel'}
                </Link>
                <p className="mt-1 text-sm text-paper-500">
                  {item.document_count} Dokumente · seit {formatDate(item.opened_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Neueste Dokumente"
        actions={
          <Link to="/dokumente" className="btn-ghost">
            Alle Dokumente
          </Link>
        }
      >
        {documents.isPending ? (
          <LoadingBlock />
        ) : documents.isError ? (
          <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
        ) : documents.data.items.length === 0 ? (
          <EmptyState
            title="Noch nichts da"
            description="Sobald ein Mailkonto synchronisiert oder ein Brief hochgeladen wurde, erscheint hier etwas."
            action={
              <Link to="/brief" className="btn-primary mt-2">
                Brief hochladen
              </Link>
            }
          />
        ) : (
          <ul className="space-y-2">
            {documents.data.items.map((document) => (
              <li key={document.id} className="card">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/dokumente/$documentId"
                    params={{ documentId: document.id }}
                    className="min-w-0 flex-1 truncate font-medium hover:underline"
                  >
                    {document.title || 'Ohne Betreff'}
                  </Link>
                  {document.doc_type ? <Badge tone="accent">{document.doc_type}</Badge> : null}
                  {document.extraction_state === 'rules_only' ? (
                    <Badge>nur Regeln</Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-paper-500">
                  {formatDate(document.document_date ?? document.created_at)}
                  {document.amount_total !== null && document.amount_total !== undefined
                    ? ` · ${formatAmount(document.amount_total, document.currency)}`
                    : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  )
}
